import { useEffect, useState, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Menu, X, ShoppingCart, Music, Calendar, Users, Mail, Home as HomeIcon, Play, MapPin, Clock, Ticket, Plus, Minus, Trash2, Check, Loader2, ArrowLeft, Camera, Sparkles, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation offset constant
const ANIMATION_OFFSET = 30;

// Animation constants to prevent re-renders
const FADE_IN_UP = { initial: { opacity: 0, y: ANIMATION_OFFSET }, animate: { opacity: 1, y: 0 }, viewport: { once: true } };
const FADE_IN_X = { initial: { opacity: 0, x: -ANIMATION_OFFSET }, animate: { opacity: 1, x: 0 }, viewport: { once: true } };
const SCROLL_INDICATOR = { animate: { y: [0, 10, 0] }, transition: { repeat: Infinity, duration: 2 } };

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = useCallback((product, size = null) => {
    setCart((prev) => {
      const existing = prev.find(
        (item) => item.product_id === product.id && item.size === size
      );
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.id && item.size === size
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          quantity: 1,
          size,
          image_url: product.image_url,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const removeFromCart = useCallback((productId, size) => {
    setCart((prev) =>
      prev.filter((item) => !(item.product_id === productId && item.size === size))
    );
  }, []);

  const updateQuantity = useCallback((productId, size, quantity) => {
    if (quantity <= 0) {
      setCart((prev) =>
        prev.filter((item) => !(item.product_id === productId && item.size === size))
      );
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.size === size
          ? { ...item, quantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const value = useMemo(() => ({
    cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { cartCount } = useCart();

  const navItems = useMemo(() => [
    { path: "/", label: "Home", icon: HomeIcon },
    { path: "/bands", label: "Bands", icon: Users },
    { path: "/tours", label: "Tours", icon: Calendar },
    { path: "/merch", label: "Merch", icon: ShoppingCart },
    { path: "/contact", label: "Contact", icon: Mail },
  ], []);

  return (
    <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
      <div className="nav-pill rounded-full px-6 py-3 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2">
          <Music className="w-6 h-6 text-gold-metallic" />
          <span className="font-heading font-bold text-gold-metallic hidden sm:block">Velvet Soul</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-gold-metallic ${
                location.pathname === item.path ? "text-gold-metallic" : "text-white/70"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative p-2 hover:text-gold-metallic transition-colors"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-gold-metallic text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2 text-white"
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-full left-0 right-0 mt-2 nav-pill rounded-2xl p-4"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? "bg-gold-metallic/10 text-gold-metallic"
                    : "text-white hover:bg-white/5"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 p-3 rounded-lg text-white hover:bg-white/5"
            >
              <ShoppingCart className="w-5 h-5" />
              Cart ({cartCount})
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-velvet-dark/50 border-t border-white/10 py-12 mt-24">
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Music className="w-8 h-8 text-gold-metallic" />
            <span className="font-heading text-2xl font-bold text-gold-metallic">Velvet Soul</span>
          </div>
          <p className="text-white/90 text-sm">San Diego&#39;s premier tribute band collective bringing the grooves of the 70s back to life.</p>
        </div>
        <div>
          <h4 className="font-heading text-gold-metallic mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-white/90">
            <li><Link to="/bands" className="hover:text-gold-metallic transition-colors">Our Bands</Link></li>
            <li><Link to="/tours" className="hover:text-gold-metallic transition-colors">Tour Dates</Link></li>
            <li><Link to="/merch" className="hover:text-gold-metallic transition-colors">Merchandise</Link></li>
            <li><Link to="/contact" className="hover:text-gold-metallic transition-colors">Book Us</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-gold-metallic mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-white/90">
            <li>San Diego, California</li>
            <li>bookings@velvetsoulshow.com</li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-gold-metallic mb-4">Follow Us</h4>
          <p className="text-white/90 text-sm">Stay connected for updates on shows and new releases.</p>
        </div>
      </div>
      <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-white/70">
        <p>© 2026 Velvet Soul Shows. All Rights Reserved.</p>
      </div>
    </div>
  </footer>
);

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen pt-32 flex items-center justify-center">
    <Loader2 className="w-12 h-12 text-gold-metallic animate-spin" />
  </div>
);

// Video data constant
const PROMO_VIDEOS = [
  { id: "velvet-soul", name: "Velvet Soul", url: "https://www.youtube.com/embed/9595OmUuixo" },
  { id: "777", name: "777 - Bruno Mars Experience", url: "https://www.youtube.com/embed/UwHb0bfXDUM" },
  { id: "run-the-world", name: "Run The World: The Premier Beyoncé Experience", url: "https://www.youtube.com/embed/cFHgn_W2AAY" },
  { id: "brown-sugar", name: "Brown Sugar: The D'Angelo Experience", url: "https://www.youtube.com/embed/WbkxQWRcWfE" },
  { id: "purple-madness", name: "The Purple Madness", url: "https://www.youtube.com/embed/rOlBhC6enjg" },
];

// Hero Section Component
const HeroSection = ({ featuredBand }) => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-velvet-dark/80 via-background/90 to-background z-10" />
    
    <div className="absolute inset-0">
      <img
        src={featuredBand?.image_url}
        alt="Velvet Soul"
        className="w-full h-full object-cover object-center opacity-60"
      />
    </div>

    <div className="relative z-20 container mx-auto px-6 text-center">
      <motion.div {...FADE_IN_UP} transition={{ duration: 0.8 }}>
        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-extrabold text-gold-metallic mb-6 tracking-tight">
          VELVET SOUL SHOWS
        </h1>
        <p className="text-xl md:text-2xl text-white mb-4 max-w-2xl mx-auto">
          SoCal&#39;s Premier Tributes and Collectives
        </p>
        <p className="text-lg text-white/90 mb-8 font-accent">
          Funk • Soul • R&B • Hip-Hop • Motown
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            data-testid="hero-book-btn"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            Book Your Event
          </Link>
          <Link
            to="/tours"
            data-testid="hero-tours-btn"
            className="btn-secondary inline-flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            View Tour Dates
          </Link>
        </div>
      </motion.div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
      <motion.div {...SCROLL_INDICATOR} className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
        <motion.div {...SCROLL_INDICATOR} className="w-1.5 h-3 bg-gold-metallic rounded-full mt-2" />
      </motion.div>
    </div>
  </section>
);

// Video Section Component
const VideoSection = () => (
  <section className="py-24 container mx-auto px-6">
    <motion.div {...FADE_IN_UP} className="text-center mb-12">
      <h2 className="font-heading text-4xl md:text-5xl font-bold text-gold-metallic mb-4">
        Experience The Groove
      </h2>
      <p className="text-white/90 max-w-xl mx-auto">
        Watch our promo videos and feel the energy of a Velvet Soul Shows performance
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {PROMO_VIDEOS.map((video, i) => (
        <motion.div
          key={video.id}
          {...FADE_IN_UP}
          transition={{ delay: i * 0.1 }}
          className="glass-card rounded-2xl overflow-hidden"
          data-testid={`video-${video.id}`}
        >
          <div className="aspect-video">
            <iframe
              src={video.url}
              title={video.name}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="p-4 text-center">
            <p className="text-gold-metallic font-heading font-bold">{video.name}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// Band Preview Card
const BandPreviewCard = ({ band, index }) => (
  <motion.div {...FADE_IN_UP} transition={{ delay: index * 0.1 }}>
    <Link
      to={`/bands/${band.id}`}
      className="block glass-card rounded-xl overflow-hidden group hover:scale-105 transition-transform duration-300"
      data-testid={`band-card-${band.id}`}
    >
      <div className="aspect-square relative overflow-hidden">
        <img
          src={band.image_url}
          alt={band.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-heading text-lg font-bold text-white">{band.name}</h3>
          <p className="text-gold-metallic text-sm font-accent">{band.genre}</p>
        </div>
      </div>
    </Link>
  </motion.div>
);

// Ticket Status Component
const TicketStatus = ({ tour }) => {
  if (tour.is_sold_out) {
    return <span className="bg-red-500/20 text-red-400 px-4 py-2 rounded-full font-medium">Sold Out</span>;
  }
  if (tour.ticket_url) {
    return (
      <a href={tour.ticket_url} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center gap-2">
        <Ticket className="w-4 h-4" />
        Get Tickets
      </a>
    );
  }
  return <span className="text-white/70 font-accent">Tickets TBA</span>;
};

// Tour Card Component
const TourCard = ({ tour, index }) => (
  <motion.div
    {...FADE_IN_X}
    transition={{ delay: index * 0.1 }}
    className="glass-card rounded-xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
    data-testid={`tour-card-${tour.id}`}
  >
    <div className="flex items-center gap-4">
      <div className="bg-gold-metallic/10 rounded-lg p-3 text-center min-w-[80px]">
        <p className="font-accent text-gold-metallic text-sm">
          {new Date(tour.date).toLocaleDateString("en-US", { month: "short" })}
        </p>
        <p className="font-heading text-2xl font-bold text-white">
          {new Date(tour.date).getDate()}
        </p>
      </div>
      <div>
        <h3 className="font-heading text-lg font-bold text-white">{tour.band_name}</h3>
        <p className="text-white/90 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          {tour.venue}, {tour.city}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-gold-metallic font-accent">{tour.price || 'TBA'}</span>
      <span className="text-white/90 flex items-center gap-1">
        <Clock className="w-4 h-4" />
        {tour.time}
      </span>
    </div>
  </motion.div>
);

// Newsletter Section Component
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.already_subscribed) {
        toast.info("You're already part of the family!");
      } else {
        toast.success('Welcome to the Velvet Soul family!');
        setIsSubscribed(true);
      }
      setEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 relative overflow-hidden" data-testid="newsletter-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-velvet-base/60 via-background to-velvet-dark/40" />
      
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-gold-metallic/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-velvet-base/20 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div {...FADE_IN_UP} className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gold-metallic/10 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-gold-metallic" />
            <span className="text-gold-metallic font-accent text-sm">Join Our Community</span>
          </div>
          
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-gold-metallic mb-4">
            Join the Velvet Soul Family!
          </h2>
          
          <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
            Get exclusive updates on shows, new merch drops, and behind-the-scenes content delivered straight to your inbox.
          </p>

          {isSubscribed ? (
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass-card rounded-2xl p-8 inline-flex flex-col items-center gap-4"
            >
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white text-lg font-medium">You&#39;re in! Check your email for a welcome message.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-gold-metallic focus:ring-2 focus:ring-gold-metallic/20 transition-all"
                data-testid="newsletter-email-input"
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Subscribe
                  </>
                )}
              </button>
            </form>
          )}

          <p className="text-white/50 text-sm mt-6">
            No spam, ever. Unsubscribe anytime.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  const [featuredBand, setFeaturedBand] = useState(null);
  const [bands, setBands] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [bandsRes, toursRes] = await Promise.all([
          axios.get(`${API}/bands`),
          axios.get(`${API}/tours`),
        ]);
        const allBands = bandsRes.data;
        setBands(allBands);
        setFeaturedBand(allBands.find((b) => b.is_featured) || allBands[0]);
        setTours(toursRes.data.slice(0, 3));
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen" data-testid="home-page">
      <HeroSection featuredBand={featuredBand} />
      <VideoSection />

      {/* Featured Bands Preview */}
      <section className="py-24 bg-velvet-dark/30">
        <div className="container mx-auto px-6">
          <motion.div {...FADE_IN_UP} className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gold-metallic mb-4">Our Bands</h2>
            <p className="text-white/90 max-w-xl mx-auto">Five unique acts, one incredible collective. From funk to R&B to Hip-Hop, we&#39;ve got your event covered.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {bands.slice(0, 5).map((band, i) => (
              <BandPreviewCard key={band.id} band={band} index={i} />
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/bands" data-testid="view-all-bands-btn" className="btn-secondary inline-flex items-center gap-2">
              <Users className="w-5 h-5" />
              View All Bands
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Shows */}
      <section className="py-24 container mx-auto px-6">
        <motion.div {...FADE_IN_UP} className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-gold-metallic mb-4">Upcoming Shows</h2>
          <p className="text-white/90 max-w-xl mx-auto">Catch us live at a venue near you</p>
        </motion.div>

        <div className="space-y-4">
          {tours.length === 0 ? (
            <p className="text-center text-white/70">No upcoming shows. Check back soon!</p>
          ) : (
            tours.map((tour, i) => <TourCard key={tour.id} tour={tour} index={i} />)
          )}
        </div>

        <div className="text-center mt-8">
          <Link to="/tours" data-testid="view-all-tours-btn" className="btn-primary inline-flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            View All Tour Dates
          </Link>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* CTA Section */}
      <section className="py-24 bg-velvet-base/40">
        <div className="container mx-auto px-6 text-center">
          <motion.div {...FADE_IN_UP}>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gold-metallic mb-4">Ready to Book?</h2>
            <p className="text-white/90 max-w-xl mx-auto mb-8">Whether it&#39;s a corporate event, wedding, or private party, we bring the groove.</p>
            <Link to="/contact" data-testid="cta-book-btn" className="btn-primary inline-flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Band Detail Component (for listing page)
const BandDetail = ({ band, index }) => (
  <motion.div
    {...FADE_IN_UP}
    transition={{ delay: 0.1 }}
    className={`flex flex-col ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 items-center`}
    data-testid={`band-detail-${band.id}`}
  >
    <div className="flex-1 w-full">
      <Link to={`/bands/${band.id}`} className="block glass-card rounded-2xl overflow-hidden disco-shimmer group">
        <img src={band.image_url} alt={band.name} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />
      </Link>
    </div>
    <div className="flex-1 space-y-4">
      <span className="font-accent text-gold-metallic text-sm">{band.role}</span>
      <Link to={`/bands/${band.id}`}>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white hover:text-gold-metallic transition-colors">{band.name}</h2>
      </Link>
      <p className="text-gold-metallic font-accent text-lg">{band.genre}</p>
      <p className="text-white leading-relaxed">{band.description}</p>
      <p className="text-white/80 flex items-center gap-2">
        <MapPin className="w-4 h-4" />
        {band.city}
      </p>
      <div className="flex gap-4 flex-wrap">
        <Link to={`/bands/${band.id}`} className="btn-primary inline-flex items-center gap-2">
          View Profile
        </Link>
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Book Now
        </Link>
      </div>
    </div>
  </motion.div>
);

// Band Profile Hero Section Component
const BandProfileHero = ({ band }) => (
  <section className="relative h-[60vh] min-h-[400px] flex items-end overflow-hidden">
    <div className="absolute inset-0">
      <img src={band.image_url} alt={band.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
    </div>
    <div className="relative z-10 container mx-auto px-6 pb-12">
      <Link to="/bands" className="inline-flex items-center gap-2 text-white/70 hover:text-gold-metallic mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to All Bands
      </Link>
      <motion.div {...FADE_IN_UP}>
        <span className="font-accent text-gold-metallic text-sm">{band.role}</span>
        <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mt-2">{band.name}</h1>
        <p className="text-gold-metallic font-accent text-xl mt-2">{band.genre}</p>
      </motion.div>
    </div>
  </section>
);

// Band Profile Bio Section Component
const BandProfileBio = ({ band }) => (
  <section className="py-16 container mx-auto px-6">
    <div className="max-w-4xl mx-auto">
      <motion.div {...FADE_IN_UP}>
        <h2 className="font-heading text-3xl font-bold text-gold-metallic mb-6">About</h2>
        <p className="text-white text-lg leading-relaxed mb-6">{band.description}</p>
        <p className="text-white/80 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-gold-metallic" />
          Based in {band.city}
        </p>
      </motion.div>
    </div>
  </section>
);

// Band Profile Gallery Component
const BandProfileGallery = ({ images, bandId, bandName }) => (
  <section className="py-16 container mx-auto px-6">
    <motion.div {...FADE_IN_UP} className="text-center mb-8">
      <h2 className="font-heading text-3xl font-bold text-gold-metallic mb-4">
        <Camera className="w-8 h-8 inline mr-2" />
        Photos From Shows
      </h2>
    </motion.div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {images.map((img, i) => (
        <motion.div
          key={`gallery-${bandId}-${i}`}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1 }}
          className="aspect-square rounded-xl overflow-hidden"
        >
          <img src={img} alt={`${bandName} performance ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
        </motion.div>
      ))}
    </div>
  </section>
);

// Band Profile Tours Section Component
const BandProfileTours = ({ tours }) => (
  <section className="py-16 bg-velvet-dark/30">
    <div className="container mx-auto px-6">
      <motion.div {...FADE_IN_UP} className="text-center mb-8">
        <h2 className="font-heading text-3xl font-bold text-gold-metallic mb-4">Upcoming Shows</h2>
      </motion.div>
      {tours.length === 0 ? (
        <p className="text-center text-white/70">No upcoming shows scheduled. Check back soon!</p>
      ) : (
        <div className="max-w-4xl mx-auto space-y-4">
          {tours.map((tour, i) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass-card rounded-xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <div className="bg-gold-metallic/10 rounded-xl p-4 text-center min-w-[80px]">
                  <p className="font-accent text-gold-metallic text-sm uppercase">
                    {new Date(tour.date).toLocaleDateString("en-US", { month: "short" })}
                  </p>
                  <p className="font-heading text-2xl font-bold text-white">{new Date(tour.date).getDate()}</p>
                </div>
                <div>
                  <p className="text-white font-heading text-lg">{tour.venue}</p>
                  <p className="text-white/70">{tour.city}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white/70 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {tour.time}
                </span>
                {tour.ticket_url && (
                  <a href={tour.ticket_url} target="_blank" rel="noopener noreferrer" className="btn-primary py-2 px-4">
                    <Ticket className="w-4 h-4 mr-2" />
                    Tickets
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  </section>
);

// Band Profile CTA Section Component
const BandProfileCTA = ({ bandName }) => (
  <section className="py-16 container mx-auto px-6">
    <div className="max-w-4xl mx-auto text-center">
      <motion.div {...FADE_IN_UP}>
        <h2 className="font-heading text-3xl font-bold text-gold-metallic mb-4">Book {bandName}</h2>
        <p className="text-white/80 mb-8">Ready to bring {bandName} to your next event? Get in touch with us today.</p>
        <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Contact for Booking
        </Link>
      </motion.div>
    </div>
  </section>
);

// Gallery images data constant
const BAND_GALLERIES = {
  'velvet-soul': [
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
    'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800',
    'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800',
    'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=800',
  ],
  '777-bruno-mars': [
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/wdryp6kq_IMG_5327.jpeg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/smawf0ak_IMG_5333.jpeg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/4coct2pa_IMG_5439.jpeg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/6wtb3qpa_IMG_5453.jpeg',
  ],
  'run-the-world': [
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/y0qwenu2_IMG_5155%20.jpg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/j6a9twaq_IMG_5124.jpg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/2rax02s4_IMG_0529.jpg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/m74wh05b_IMG_0534.jpg',
  ],
  'brown-sugar': [
    'https://images.unsplash.com/photo-1415201364774-f6f0bb35f28f?w=800',
    'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800',
    'https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=800',
    'https://images.unsplash.com/photo-1446057032654-9d8885db76c6?w=800',
  ],
  'purple-madness': [
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/c7uuogxr_IMG_2938%20%281%29.JPG',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/ztpedml8_IMG_2943%20%281%29.JPG',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/8fnoim6w_IMG_6873%20%281%29.jpg',
    'https://customer-assets.emergentagent.com/job_velvet-soul-sd/artifacts/ilzyouee_Photo%20Apr%2001%202024%2C%201%2019%2014%20PM.jpg',
  ],
};

// Individual Band Profile Page
const BandProfilePage = () => {
  const { bandId } = useParams();
  const [band, setBand] = useState(null);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  const galleryImages = useMemo(() => 
    BAND_GALLERIES[bandId] || BAND_GALLERIES['velvet-soul'],
    [bandId]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bandRes, toursRes] = await Promise.all([
          axios.get(`${API}/bands/${bandId}`),
          axios.get(`${API}/tours/band/${bandId}`),
        ]);
        setBand(bandRes.data);
        setTours(toursRes.data);
      } catch (err) {
        toast.error("Failed to load band data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bandId]);

  if (loading) return <LoadingSpinner />;
  if (!band) return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-heading text-4xl font-bold text-white mb-4">Band Not Found</h1>
        <Link to="/bands" className="btn-primary">View All Bands</Link>
      </div>
    </div>
  );

  const promoVideo = PROMO_VIDEOS.find(v => v.id === bandId || (bandId === '777-bruno-mars' && v.id === '777'));

  return (
    <div className="min-h-screen pt-24 pb-24" data-testid={`band-profile-${bandId}`}>
      <BandProfileHero band={band} />
      <BandProfileBio band={band} />

      {/* Promo Video Section */}
      {promoVideo && (
        <section className="py-16 bg-velvet-dark/30">
          <div className="container mx-auto px-6">
            <motion.div {...FADE_IN_UP} className="text-center mb-8">
              <h2 className="font-heading text-3xl font-bold text-gold-metallic mb-4">Watch Us Perform</h2>
            </motion.div>
            <div className="max-w-4xl mx-auto">
              <div className="glass-card rounded-2xl overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    src={promoVideo.url}
                    title={band.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <BandProfileGallery images={galleryImages} bandId={bandId} bandName={band.name} />
      <BandProfileTours tours={tours} />
      <BandProfileCTA bandName={band.name} />
    </div>
  );
};

// Bands Page
const BandsPage = () => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBands = async () => {
      try {
        const res = await axios.get(`${API}/bands`);
        setBands(res.data);
      } catch (err) {
        toast.error("Failed to load bands");
      } finally {
        setLoading(false);
      }
    };
    fetchBands();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="bands-page">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_IN_UP} className="text-center mb-16">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-gold-metallic mb-4">Our Bands</h1>
          <p className="text-white/90 max-w-xl mx-auto">Five unique acts, one incredible collective. From funk to R&B to Hip-Hop, we&#39;ve got your event covered.</p>
        </motion.div>

        <div className="space-y-24">
          {bands.map((band, i) => <BandDetail key={band.id} band={band} index={i} />)}
        </div>
      </div>
    </div>
  );
};

// Tours Page
const ToursPage = () => {
  const [tours, setTours] = useState([]);
  const [bands, setBands] = useState([]);
  const [selectedBand, setSelectedBand] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursRes, bandsRes] = await Promise.all([
          axios.get(`${API}/tours`),
          axios.get(`${API}/bands`),
        ]);
        setTours(toursRes.data);
        setBands(bandsRes.data);
      } catch (err) {
        toast.error("Failed to load tour data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredTours = useMemo(() => 
    selectedBand === "all" ? tours : tours.filter((t) => t.band_id === selectedBand),
    [tours, selectedBand]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="tours-page">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_IN_UP} className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-gold-metallic mb-4">Tour Dates</h1>
          <p className="text-white/90 max-w-xl mx-auto">Catch us live at a venue near you</p>
        </motion.div>

        {/* Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <button
            onClick={() => setSelectedBand("all")}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              selectedBand === "all" ? "bg-gold-metallic text-black" : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            All Bands
          </button>
          {bands.map((band) => (
            <button
              key={band.id}
              onClick={() => setSelectedBand(band.id)}
              data-testid={`filter-${band.id}`}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedBand === band.id ? "bg-gold-metallic text-black" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {band.name}
            </button>
          ))}
        </div>

        {/* Tours List */}
        <div className="space-y-4">
          {filteredTours.length === 0 ? (
            <div className="text-center py-12 text-white/90">No upcoming shows for this band. Check back soon!</div>
          ) : (
            filteredTours.map((tour, i) => (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-xl p-6"
                data-testid={`tour-item-${tour.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="bg-gold-metallic/10 rounded-xl p-4 text-center min-w-[100px]">
                      <p className="font-accent text-gold-metallic text-sm uppercase">
                        {new Date(tour.date).toLocaleDateString("en-US", { month: "short" })}
                      </p>
                      <p className="font-heading text-3xl font-bold text-white">{new Date(tour.date).getDate()}</p>
                      <p className="text-white/70 text-sm">{new Date(tour.date).getFullYear()}</p>
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white mb-1">{tour.band_name}</h3>
                      <p className="text-white flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-gold-metallic" />
                        {tour.venue}
                      </p>
                      <p className="text-white/80 text-sm">{tour.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-gold-metallic font-heading text-lg font-bold">{tour.price || "TBA"}</p>
                      <p className="text-white/80 flex items-center gap-1 justify-end">
                        <Clock className="w-4 h-4" />
                        {tour.time}
                      </p>
                    </div>
                    <TicketStatus tour={tour} />
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, index }) => {
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || null);
  const { addToCart } = useCart();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="glass-card rounded-xl overflow-hidden group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-[3/4] relative overflow-hidden bg-muted">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
        {!product.in_stock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-heading text-xl">Out of Stock</span>
          </div>
        )}
      </div>
      <div className="p-6">
        <p className="text-gold-metallic font-accent text-sm mb-1">{product.category}</p>
        <h3 className="font-heading text-xl font-bold text-white mb-2">{product.name}</h3>
        <p className="text-white/90 text-sm mb-4 line-clamp-2">{product.description}</p>
        
        {product.sizes?.length > 0 && (
          <div className="mb-4">
            <p className="text-white/70 text-sm mb-2">Size</p>
            <div className="flex gap-2 flex-wrap">
              {product.sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  data-testid={`size-${size}`}
                  className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                    selectedSize === size ? "bg-gold-metallic text-black" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="font-heading text-2xl font-bold text-gold-metallic">${product.price.toFixed(2)}</span>
          <button
            onClick={() => addToCart(product, selectedSize)}
            disabled={!product.in_stock}
            data-testid={`add-to-cart-${product.id}`}
            className="btn-primary py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Merch Page
const MerchPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/merch`);
        setProducts(res.data);
      } catch (err) {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = useMemo(() => ["all", ...new Set(products.map((p) => p.category))], [products]);
  const filteredProducts = useMemo(() => 
    selectedCategory === "all" ? products : products.filter((p) => p.category === selectedCategory),
    [products, selectedCategory]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="merch-page">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_IN_UP} className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-gold-metallic mb-4">Merchandise</h1>
          <p className="text-white/90 max-w-xl mx-auto">Rep the groove with official Velvet Soul merch</p>
        </motion.div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat}`}
              className={`px-4 py-2 rounded-full font-medium capitalize transition-all ${
                selectedCategory === cat ? "bg-gold-metallic text-black" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Cart Page
const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        cart_items: cart,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Failed to create checkout session");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="cart-page">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div {...FADE_IN_UP} className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-gold-metallic mb-4">Your Cart</h1>
        </motion.div>

        {cart.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/90 mb-8">Your cart is empty</p>
            <Link to="/merch" className="btn-primary inline-flex items-center gap-2">Continue Shopping</Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {cart.map((item) => (
                <motion.div
                  key={`${item.product_id}-${item.size}`}
                  layout
                  className="glass-card rounded-xl p-4 flex gap-4"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img src={item.image_url} alt={item.product_name} className="w-24 h-24 object-cover rounded-lg" />
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-white">{item.product_name}</h3>
                    {item.size && <p className="text-white/90 text-sm">Size: {item.size}</p>}
                    <p className="text-gold-metallic font-heading">${item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                      className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"
                      data-testid="decrease-qty"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-accent">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                      className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"
                      data-testid="increase-qty"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removeFromCart(item.product_id, item.size)}
                      className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center hover:bg-red-500/30 ml-2"
                      data-testid="remove-item"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="glass-card rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <span className="text-white/90">Subtotal</span>
                <span className="font-heading text-2xl font-bold text-gold-metallic">${cartTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={handleCheckout}
                disabled={loading}
                data-testid="checkout-btn"
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" />Proceed to Checkout</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Checkout Success Page
const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const { clearCart } = useCart();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          clearCart();
          return;
        } else if (res.data.status === "expired") {
          setStatus("error");
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        setStatus("error");
      }
    };

    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="container mx-auto px-6 max-w-lg text-center">
        {status === "loading" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="w-16 h-16 text-gold-metallic animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-white mb-4">Processing Payment...</h1>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-gold-metallic mb-4">Thank You!</h1>
            <p className="text-white/90 mb-8">Your order has been confirmed. We&#39;ll send you an email with your order details.</p>
            <Link to="/merch" className="btn-primary inline-flex items-center gap-2">Continue Shopping</Link>
          </motion.div>
        )}

        {status === "error" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-white mb-4">Payment Failed</h1>
            <p className="text-white/90 mb-8">Something went wrong with your payment. Please try again.</p>
            <Link to="/cart" className="btn-primary inline-flex items-center gap-2">Return to Cart</Link>
          </motion.div>
        )}

        {status === "pending" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <h1 className="font-heading text-3xl font-bold text-white mb-4">Payment Processing</h1>
            <p className="text-white/90 mb-8">Your payment is being processed. Please check your email for confirmation.</p>
            <Link to="/" className="btn-secondary inline-flex items-center gap-2">Return Home</Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", event_type: "", event_date: "", venue: "", message: "", band_preference: "",
  });

  useEffect(() => {
    axios.get(`${API}/bands`).then(res => setBands(res.data)).catch(() => toast.error("Failed to load bands"));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Booking inquiry submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="contact-success">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-gold-metallic mb-4">Inquiry Received!</h1>
          <p className="text-white/90 mb-8">Thank you for your interest. We&#39;ll get back to you within 24-48 hours.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2"><HomeIcon className="w-5 h-5" />Return Home</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="contact-page">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div {...FADE_IN_UP} className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-gold-metallic mb-4">Book Us</h1>
          <p className="text-white/90 max-w-xl mx-auto">Ready to bring the groove to your event? Fill out the form below and we&#39;ll get back to you.</p>
        </motion.div>

        <motion.form {...FADE_IN_UP} transition={{ delay: 0.2 }} onSubmit={handleSubmit} className="glass-card rounded-2xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-white/90 text-sm mb-2">Your Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="input-name" className="input-underline" placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-white/90 text-sm mb-2">Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="input-email" className="input-underline" placeholder="john@example.com" />
            </div>
            <div>
              <label className="block text-white/90 text-sm mb-2">Phone Number *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required data-testid="input-phone" className="input-underline" placeholder="(555) 123-4567" />
            </div>
            <div>
              <label className="block text-white/90 text-sm mb-2">Event Type *</label>
              <select name="event_type" value={formData.event_type} onChange={handleChange} required data-testid="input-event-type" className="input-underline bg-transparent">
                <option value="" className="bg-card">Select event type</option>
                <option value="wedding" className="bg-card">Wedding</option>
                <option value="corporate" className="bg-card">Corporate Event</option>
                <option value="private_party" className="bg-card">Private Party</option>
                <option value="festival" className="bg-card">Festival</option>
                <option value="club" className="bg-card">Club/Venue</option>
                <option value="other" className="bg-card">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-white/90 text-sm mb-2">Event Date *</label>
              <input type="date" name="event_date" value={formData.event_date} onChange={handleChange} required data-testid="input-event-date" className="input-underline" />
            </div>
            <div>
              <label className="block text-white/90 text-sm mb-2">Venue/Location *</label>
              <input type="text" name="venue" value={formData.venue} onChange={handleChange} required data-testid="input-venue" className="input-underline" placeholder="Venue name and city" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-white/90 text-sm mb-2">Preferred Band</label>
              <select name="band_preference" value={formData.band_preference} onChange={handleChange} data-testid="input-band-preference" className="input-underline bg-transparent">
                <option value="" className="bg-card">No preference</option>
                {bands.map((band) => (<option key={band.id} value={band.name} className="bg-card">{band.name} ({band.genre})</option>))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-white/90 text-sm mb-2">Message *</label>
              <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} data-testid="input-message" className="input-underline resize-none" placeholder="Tell us about your event..." />
            </div>
          </div>

          <div className="mt-8 text-center">
            <button type="submit" disabled={loading} data-testid="submit-inquiry-btn" className="btn-primary inline-flex items-center gap-2">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Mail className="w-5 h-5" />Submit Inquiry</>}
            </button>
          </div>
        </motion.form>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card rounded-xl p-6">
            <h3 className="font-heading text-xl font-bold text-gold-metallic mb-4">Direct Contact</h3>
            <div className="mb-4 pb-4 border-b border-white/20">
              <p className="font-heading text-lg text-white font-semibold">Julian Davis</p>
              <p className="text-gold-metallic font-accent text-sm">Owner & Producer</p>
            </div>
            <p className="text-white mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4 text-gold-metallic" />
              bookings@velvetsoulshow.com
            </p>
            <p className="text-white mb-2 flex items-center gap-2">
              <span className="text-gold-metallic">📞</span>
              619-880-1277
            </p>
            <p className="text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold-metallic" />
              San Diego, California
            </p>
          </div>
          <div className="glass-card rounded-xl p-6">
            <h3 className="font-heading text-xl font-bold text-gold-metallic mb-4">Response Time</h3>
            <p className="text-white">We typically respond to all inquiries within 24-48 hours. For urgent requests, please mention it in your message.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Page - Tour Form Component
const TourForm = ({ bands, editingTour, setEditingTour, onSuccess }) => {
  const [formData, setFormData] = useState({
    band_id: "", band_name: "", venue: "", city: "", date: "", time: "", price: "", ticket_url: "", is_sold_out: false,
  });

  useEffect(() => {
    if (editingTour) {
      setFormData(editingTour);
    } else {
      setFormData({ band_id: "", band_name: "", venue: "", city: "", date: "", time: "", price: "", ticket_url: "", is_sold_out: false });
    }
  }, [editingTour]);

  const handleBandSelect = (bandId) => {
    const band = bands.find((b) => b.id === bandId);
    if (band) {
      setFormData((prev) => ({ ...prev, band_id: bandId, band_name: band.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTour) {
        await axios.put(`${API}/tours/${editingTour.id}`, formData);
        toast.success("Tour updated successfully!");
      } else {
        await axios.post(`${API}/tours`, formData);
        toast.success("Tour created successfully!");
      }
      setEditingTour(null);
      onSuccess();
    } catch (err) {
      toast.error(editingTour ? "Failed to update tour" : "Failed to create tour");
    }
  };

  return (
    <div className="glass-card rounded-xl p-6">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">{editingTour ? "Edit Tour" : "Add New Tour"}</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-white/90 text-sm mb-2">Band *</label>
            <select value={formData.band_id} onChange={(e) => handleBandSelect(e.target.value)} required data-testid="tour-band-select" className="input-underline bg-transparent">
              <option value="" className="bg-card">Select band</option>
              {bands.map((band) => (<option key={band.id} value={band.id} className="bg-card">{band.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">Venue *</label>
            <input type="text" value={formData.venue} onChange={(e) => setFormData((prev) => ({ ...prev, venue: e.target.value }))} required data-testid="tour-venue-input" className="input-underline" placeholder="Venue name" />
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">City *</label>
            <input type="text" value={formData.city} onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))} required data-testid="tour-city-input" className="input-underline" placeholder="City, State" />
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">Date *</label>
            <input type="date" value={formData.date} onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))} required data-testid="tour-date-input" className="input-underline" />
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">Time *</label>
            <input type="text" value={formData.time} onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))} required data-testid="tour-time-input" className="input-underline" placeholder="8:00 PM" />
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">Price</label>
            <input type="text" value={formData.price} onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))} data-testid="tour-price-input" className="input-underline" placeholder="$25" />
          </div>
          <div>
            <label className="block text-white/90 text-sm mb-2">Ticket URL</label>
            <input type="url" value={formData.ticket_url} onChange={(e) => setFormData((prev) => ({ ...prev, ticket_url: e.target.value }))} data-testid="tour-ticket-url-input" className="input-underline" placeholder="https://..." />
          </div>
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_sold_out} onChange={(e) => setFormData((prev) => ({ ...prev, is_sold_out: e.target.checked }))} data-testid="tour-sold-out-checkbox" className="w-5 h-5 rounded bg-white/10" />
              <span className="text-white/90">Sold Out</span>
            </label>
          </div>
        </div>
        <div className="flex gap-4 mt-6">
          <button type="submit" data-testid="save-tour-btn" className="btn-primary">{editingTour ? "Update Tour" : "Add Tour"}</button>
          {editingTour && (<button type="button" onClick={() => setEditingTour(null)} className="btn-secondary">Cancel</button>)}
        </div>
      </form>
    </div>
  );
};

// Admin Page - Tours List Component
const ToursList = ({ tours, onEdit, onDelete }) => (
  <div className="glass-card rounded-xl p-6">
    <h2 className="font-heading text-2xl font-bold text-white mb-6">All Tours</h2>
    <div className="space-y-4">
      {tours.map((tour) => (
        <div key={tour.id} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-white/5 rounded-lg" data-testid={`admin-tour-${tour.id}`}>
          <div>
            <p className="font-heading text-lg text-white">{tour.band_name}</p>
            <p className="text-white/90 text-sm">{tour.venue}, {tour.city} • {tour.date} @ {tour.time}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onEdit(tour)} data-testid={`edit-tour-${tour.id}`} className="px-4 py-2 bg-gold-metallic/20 text-gold-metallic rounded-lg hover:bg-gold-metallic/30">Edit</button>
            <button onClick={() => onDelete(tour.id)} data-testid={`delete-tour-${tour.id}`} className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30">Delete</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Admin Page
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("tours");
  const [bands, setBands] = useState([]);
  const [tours, setTours] = useState([]);
  const [merch, setMerch] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTour, setEditingTour] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [bandsRes, toursRes, merchRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/bands`),
        axios.get(`${API}/tours`),
        axios.get(`${API}/merch`),
        axios.get(`${API}/inquiries`),
      ]);
      setBands(bandsRes.data);
      setTours(toursRes.data);
      setMerch(merchRes.data);
      setInquiries(inquiriesRes.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteTour = async (tourId) => {
    if (!window.confirm("Are you sure you want to delete this tour?")) return;
    try {
      await axios.delete(`${API}/tours/${tourId}`);
      toast.success("Tour deleted successfully!");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete tour");
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="admin-page">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_IN_UP} className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-gold-metallic mb-2">Admin Panel</h1>
          <p className="text-white/90">Manage tours, bands, merch, and booking inquiries</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {["tours", "bands", "merch", "inquiries"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`admin-tab-${tab}`}
              className={`px-4 py-2 rounded-full font-medium capitalize transition-all ${
                activeTab === tab ? "bg-gold-metallic text-black" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tours Management */}
        {activeTab === "tours" && (
          <div className="space-y-8">
            <TourForm bands={bands} editingTour={editingTour} setEditingTour={setEditingTour} onSuccess={fetchData} />
            <ToursList tours={tours} onEdit={setEditingTour} onDelete={handleDeleteTour} />
          </div>
        )}

        {/* Bands List */}
        {activeTab === "bands" && (
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">All Bands</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bands.map((band) => (
                <div key={band.id} className="flex items-center gap-4 p-4 bg-white/5 rounded-lg" data-testid={`admin-band-${band.id}`}>
                  <img src={band.image_url} alt={band.name} className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <p className="font-heading text-lg text-white">{band.name}</p>
                    <p className="text-gold-metallic text-sm font-accent">{band.genre}</p>
                    <p className="text-white/70 text-sm">{band.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Merch List */}
        {activeTab === "merch" && (
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">All Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {merch.map((product) => (
                <div key={product.id} className="p-4 bg-white/5 rounded-lg" data-testid={`admin-product-${product.id}`}>
                  <img src={product.image_url} alt={product.name} className="w-full aspect-square object-cover rounded-lg mb-4" />
                  <p className="font-heading text-lg text-white">{product.name}</p>
                  <p className="text-gold-metallic font-heading">${product.price.toFixed(2)}</p>
                  <p className="text-white/70 text-sm">{product.category}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inquiries List */}
        {activeTab === "inquiries" && (
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Booking Inquiries</h2>
            <div className="space-y-4">
              {inquiries.length === 0 ? (
                <p className="text-white/90 text-center py-8">No booking inquiries yet</p>
              ) : (
                inquiries.map((inquiry) => (
                  <div key={inquiry.id} className="p-4 bg-white/5 rounded-lg" data-testid={`admin-inquiry-${inquiry.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div>
                        <p className="font-heading text-lg text-white">{inquiry.name}</p>
                        <p className="text-gold-metallic text-sm">{inquiry.email}</p>
                        <p className="text-white/90 text-sm">{inquiry.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/90 text-sm">{inquiry.event_type}</p>
                        <p className="text-gold-metallic font-accent">{inquiry.event_date}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/90 text-sm"><strong>Venue:</strong> {inquiry.venue}</p>
                      {inquiry.band_preference && <p className="text-white/90 text-sm"><strong>Band Preference:</strong> {inquiry.band_preference}</p>}
                      <p className="text-white/90 text-sm mt-2">{inquiry.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-background">
          <Navigation />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/bands" element={<BandsPage />} />
            <Route path="/bands/:bandId" element={<BandProfilePage />} />
            <Route path="/tours" element={<ToursPage />} />
            <Route path="/merch" element={<MerchPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
          <Footer />
          <Toaster position="top-right" richColors theme="dark" />
        </div>
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
